import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UserPlus, ShieldCheck, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const ROLES = [
  { v: "store_manager", l: "Store Manager" },
  { v: "procurement_officer", l: "Procurement Officer" },
  { v: "quality_officer", l: "Quality/Compliance Officer" },
  { v: "technician", l: "Technician" },
  { v: "center_staff", l: "Center Staff" },
  { v: "field_staff", l: "Field Staff / Phlebotomist" },
];
const ROLE_LABEL = { super_admin: "Super Admin", ...Object.fromEntries(ROLES.map((r) => [r.v, r.l])) };

const EMPLOYEE_ID_RE = /^INT\d{4}$/;
const isStrongPassword = (v) => v.length >= 8 && /[A-Z]/.test(v) && /[^A-Za-z0-9]/.test(v);

export default function Users() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const focusLocationId = searchParams.get("location") || "";

  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => api.get("/locations").then((r) => r.data) });
  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: () => api.get("/departments").then((r) => r.data) });

  const [open, setOpen] = useState(false);
  const blank = { name: "", employee_id: "", phone: "", email: "", password: "", role: "store_manager", location_id: "", department: "" };
  const [form, setForm] = useState(() => (focusLocationId ? { ...blank, location_id: focusLocationId } : blank));
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const blankEdit = { name: "", employee_id: "", phone: "", email: "", password: "", role: "store_manager", location_id: "", department: "" };
  const [editForm, setEditForm] = useState(blankEdit);

  const create = async () => {
    const employeeId = form.employee_id.trim().toUpperCase();
    if (!EMPLOYEE_ID_RE.test(employeeId)) {
      toast.error("Employee ID must look like INT0001 — copy it exactly from HRM Employee Master.");
      return;
    }
    if (!isStrongPassword(form.password)) {
      toast.error("Password must be at least 8 characters with one capital letter and one special character.");
      return;
    }
    try {
      await api.post("/users", { ...form, employee_id: employeeId, location_id: form.location_id || null });
      toast.success("User account created");
      setOpen(false); setForm(blank);
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      name: u.name || "", employee_id: u.employee_id || "", phone: u.phone || "", email: u.email || "", password: "",
      role: u.role, location_id: u.location_id || "", department: u.department || "",
    });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    const employeeId = editForm.employee_id.trim().toUpperCase();
    if (!EMPLOYEE_ID_RE.test(employeeId)) {
      toast.error("Employee ID must look like INT0001 — copy it exactly from HRM Employee Master.");
      return;
    }
    if (editForm.password && !isStrongPassword(editForm.password)) {
      toast.error("Password must be at least 8 characters with one capital letter and one special character.");
      return;
    }
    try {
      const payload = { ...editForm, employee_id: employeeId, location_id: editForm.location_id || null };
      if (!payload.password) delete payload.password; // blank = keep current password
      if (editTarget.role === "super_admin") delete payload.role; // role locked for the Owner account
      await api.put(`/users/${editTarget._id}`, payload);
      toast.success("User details updated");
      setEditTarget(null);
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u._id}`, { active: !u.active });
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      toast.success(u.active ? "User deactivated" : "User reactivated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget._id}`);
      toast.success("User account permanently deleted");
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDeleteTarget(null);
  };

  const focusLocation = locations.find((l) => l._id === focusLocationId);

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-navy">User Management</h2>
          <p className="text-slate-500 text-sm mt-1">
            <ShieldCheck size={14} className="inline mr-1" style={{ color: "#0a8f8f" }} />
            Only the Super Admin can create, edit, disable or delete accounts.
          </p>
          {focusLocation && (
            <p className="text-xs mt-1.5 px-2.5 py-1 rounded-md inline-block" style={{ background: "#FEF3C7", color: "#92400E" }}>
              "{focusLocation.name}" has no active staff — use "Create User" below to assign someone (location is pre-filled).
            </p>
          )}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-add-user-button" style={{ background: "#042C53" }} className="text-white">
              <UserPlus size={16} className="mr-2" /> Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Create User Account</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Full Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div>
                <Label>Employee ID (login ID)</Label>
                <Input data-testid="user-employee-id" placeholder="INT0001" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} />
                <p className="text-xs text-slate-400 mt-1">Copy exactly from HRM Employee Master — this is what they'll type in to sign in.</p>
              </div>
              <div><Label>Phone (contact only, optional)</Label><Input data-testid="user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email (optional)</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Password</Label>
                <Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <p className="text-xs text-slate-400 mt-1">Minimum 8 characters, at least one capital letter and one special character. Share this with the employee yourself.</p>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location (scope)</Label>
                <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                  <SelectTrigger data-testid="user-location"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="bg-white">{locations.map((l) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Department (for technicians)</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="user-department"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {departments.filter((d) => d.active).map((d) => <SelectItem key={d._id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} data-testid="user-submit" style={{ background: "#042C53" }} className="text-white">Create Account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Employee ID</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Active</th>
                <th className="px-4 py-3 font-semibold text-right">Edit</th>
                <th className="px-4 py-3 font-semibold text-right">Delete</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={u._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-navy">
                    {u.name}
                    {u.role === "super_admin" && <span className="ml-2 text-[10px] font-bold text-white px-2 py-0.5 rounded-full" style={{ background: "#0a8f8f" }}>OWNER</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 font-mono">{u.employee_id}</td>
                  <td className="px-4 py-3 text-slate-600">{ROLE_LABEL[u.role]}</td>
                  <td className="px-4 py-3 text-slate-600">{u.location_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: u.active ? "#10B981" : "#94A3B8" }}>
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Switch
                      checked={u.active}
                      disabled={u.role === "super_admin"}
                      onCheckedChange={() => toggleActive(u)}
                      data-testid={`user-toggle-${idx}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => openEdit(u)} data-testid={`user-edit-${idx}`}
                      className="text-slate-400 hover:text-teal hover:bg-slate-100"
                    >
                      <Pencil size={16} />
                    </Button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon" disabled={u.role === "super_admin"}
                      onClick={() => setDeleteTarget(u)} data-testid={`user-delete-${idx}`}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Edit {editTarget?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Full Name</Label><Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div>
              <Label>Employee ID (login ID)</Label>
              <Input data-testid="edit-user-employee-id" placeholder="INT0001" value={editForm.employee_id} onChange={(e) => setEditForm({ ...editForm, employee_id: e.target.value })} />
            </div>
            <div><Label>Phone (contact only, optional)</Label><Input data-testid="edit-user-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>New Password</Label>
              <Input data-testid="edit-user-password" placeholder="Leave blank to keep current" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
              <p className="text-xs text-slate-400 mt-1">Minimum 8 characters, at least one capital letter and one special character.</p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={editTarget?.role === "super_admin"}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
              {editTarget?.role === "super_admin" && <p className="text-xs text-slate-400 mt-1">The Owner's role can't be changed.</p>}
            </div>
            <div>
              <Label>Location (scope)</Label>
              <Select value={editForm.location_id} onValueChange={(v) => setEditForm({ ...editForm, location_id: v })}>
                <SelectTrigger data-testid="edit-user-location"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent className="bg-white">{locations.map((l) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Use this to shift staff to a different center or lab.</p>
            </div>
            <div className="col-span-2">
              <Label>Department (for technicians)</Label>
              <Select value={editForm.department} onValueChange={(v) => setEditForm({ ...editForm, department: v })}>
                <SelectTrigger data-testid="edit-user-department"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {departments.filter((d) => d.active).map((d) => <SelectItem key={d._id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="edit-user-submit" style={{ background: "#042C53" }} className="text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the account entirely — it cannot be undone. Only allowed when the
              account has no requisition or stock-transaction history. If this account has been
              used, delete will be blocked and you should disable it instead, which keeps the
              audit trail intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete} data-testid="user-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
